import { useContext, useEffect } from 'react';
import { PermissionContext } from '../../ui';
import { useActivityCounts } from './activitiesQueries';

/** The view keys either of which lets row 3 answer; with neither it is a `403` (F3-a Wave 1 contract row 3). */
const VIEW_KEYS = ['account.view', 'lead.view'] as const;

/**
 * The signed-in user's overdue follow-ups, for the navigation badge (F3-a spec FR-V7, AC-23, AC-48; F3-a
 * WI-2.2 plan Decision 19): row 3 under *Mine*, through the Activities page's own
 * `useActivityCounts('mine')`, so every follow-up write — which invalidates `countsAll()` — refreshes it.
 *
 * - **Read only for a role holding `account.view` or `lead.view`**: without either, row 3 answers `403`,
 *   which would make the session re-read `/me` on every page.
 * - **Re-read when the window gains focus and when the document becomes visible**, the triggers
 *   `SessionProvider` uses. The app's client keeps `refetchOnWindowFocus: false`, so the badge asks for
 *   its own; `cancelRefetch: false` joins a read already in flight, so a tab switch, which fires both
 *   events, sends one request.
 * - **A failed re-read keeps the last count**: TanStack keeps the last data
 *   beside the error, so the badge stays as it was rather than blanking.
 *
 * Returns the overdue count, or `null` while it is unknown (not yet read, or the first read failed) or not
 * permitted — no badge then.
 */
export function useOverdueFollowUps(): number | null {
    const resolve = useContext(PermissionContext);
    const permitted = VIEW_KEYS.some((key) => resolve(key).kind === 'allowed');
    const query = useActivityCounts('mine', { enabled: permitted });
    const { refetch } = query;

    useEffect(() => {
        if (!permitted) {
            return undefined;
        }
        const reRead = () => {
            void refetch({ cancelRefetch: false });
        };
        const onVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                reRead();
            }
        };
        window.addEventListener('focus', reRead);
        document.addEventListener('visibilitychange', onVisibilityChange);
        return () => {
            window.removeEventListener('focus', reRead);
            document.removeEventListener('visibilitychange', onVisibilityChange);
        };
    }, [permitted, refetch]);

    return permitted && query.data !== undefined ? query.data.overdue : null;
}
